import React, { useEffect, useState, useRef } from 'react';
import { collection, query, orderBy, onSnapshot, serverTimestamp, updateDoc, doc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { cabeceraSesion } from '../utils/sesionPanel';

/*
 📁 Firestore Structure:
 
 Collection: chats
 └─ Document: {phone} (e.g., "+593994633688")
    ├─ name: "Cliente Name"
    ├─ lastMessage: "Hola"
    ├─ lastMessageAt: Timestamp
    ├─ unread: true
    └─ Subcollection: messages
       └─ Document: {autoId}
          ├─ messageId: "6961f2bfe377112f3ecf6bef"
          ├─ from: "client" | "agent" | "system"
          ├─ body: "Hola"
          ├─ type: "text"
          ├─ timestamp: Timestamp
          └─ status: "received" | "sent" | "read"
*/

function formatTime(ts, timeStr) {
  try {
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    // Check if date is valid
    if (!isNaN(d.getTime())) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  } catch (e) {
    // Fallback to timeStr if date parsing fails
  }

  // Fallback: If timeStr is available strings "HH:MM:SS"
  if (timeStr && typeof timeStr === 'string' && timeStr.includes(':')) {
    return timeStr.substring(0, 5);
  }

  return '';
}

function ChatList({ chats, selectedPhone, onSelectPhone }) {
  return (
    <div style={{ flex: '0 0 200px', borderRight: '1px solid #e5e7eb', overflowY: 'auto', background: '#fff' }}>
      <div style={{ padding: 12, fontSize: 14, fontWeight: 700, borderBottom: '1px solid #e5e7eb' }}>💬 Chats</div>
      {chats.length === 0 && (
        <div style={{ padding: 12, fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>No hay chats</div>
      )}
      {chats.map(chat => (
        <div
          key={chat.phone}
          onClick={() => onSelectPhone(chat.phone)}
          style={{
            padding: '10px 12px',
            borderBottom: '1px solid #f3f4f6',
            cursor: 'pointer',
            background: selectedPhone === chat.phone ? '#e8eaf6' : 'white',
            borderLeft: selectedPhone === chat.phone ? '4px solid #3b82f6' : '4px solid transparent',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => { if (selectedPhone !== chat.phone) e.currentTarget.style.background = '#f9fafb'; }}
          onMouseLeave={(e) => { if (selectedPhone !== chat.phone) e.currentTarget.style.background = 'white'; }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', marginBottom: 4 }}>{chat.name}</div>
          <div style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 4 }}>{chat.lastMessage}</div>
          <div style={{ fontSize: 11, color: '#9ca3af', display: 'flex', justifyContent: 'space-between' }}>
            <span>{formatTime(chat.lastMessageAt)}</span>
            {chat.unread && <span style={{ background: '#3b82f6', color: 'white', padding: '2px 6px', borderRadius: 10, fontSize: 10 }}>●</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function MessageList({ messages, currentAgent }) {
  const listRef = useRef(null);

  useEffect(() => {
    setTimeout(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    }, 80);
  }, [messages]);

  return (
    <div
      ref={listRef}
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: 12,
        background: '#e5ddd3',
        display: 'flex',
        flexDirection: 'column',
        gap: 8
      }}
    >
      {messages.length === 0 && (
        <div style={{ textAlign: 'center', color: '#9ca3af', marginTop: 40 }}>
          <div style={{ fontSize: 24 }}>💬</div>
          <div>No hay mensajes</div>
          <div style={{ fontSize: 12 }}>Los mensajes de WhatsApp aparecerán aquí en tiempo real</div>
        </div>
      )}

      {messages.map(msg => {
        const isClient = msg.from === 'client';
        return (
          <div
            key={msg.id}
            style={{
              display: 'flex',
              justifyContent: isClient ? 'flex-start' : 'flex-end'
            }}
          >
            <div
              style={{
                maxWidth: '70%',
                padding: '8px 12px',
                borderRadius: 8,
                background: isClient ? 'white' : '#dcf8c6',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                wordWrap: 'break-word'
              }}
            >
              <div style={{ fontSize: 13, color: '#111827', whiteSpace: 'pre-wrap' }}>{msg.body}</div>
              <div
                style={{
                  fontSize: 11,
                  color: '#6b7280',
                  marginTop: 6,
                  display: 'flex',
                  gap: 6,
                  justifyContent: 'flex-end'
                }}
              >
                <span>{formatTime(msg.timestamp, msg.time)}</span>
                {!isClient && <span title={msg.status}>{msg.status === 'read' ? '✓✓' : '✓'}</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ChatPanel() {
  const [chats, setChats] = useState([]);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const getWhatsAppSendUrl = () => {
    // En producción (HTTPS) usar proxy para evitar Mixed Content; en dev usar URL directa
    if (process.env.REACT_APP_WHATSAPP_SEND_URL) return process.env.REACT_APP_WHATSAPP_SEND_URL;
    return '/api/whatsapp/send';
  };

  const normalizarTelefonoParaWhatsAppApi = (telefonoRaw) => {
    const digits = (telefonoRaw ?? '').toString().replace(/\D/g, '');
    if (!digits) return '';
    if (digits.startsWith('593')) return digits; // ya viene completo
    if (digits.startsWith('0') && digits.length === 10) return `593${digits.substring(1)}`;
    if (!digits.startsWith('0') && digits.length === 9) return `593${digits}`;
    return digits;
  };

  const currentAgent = (() => {
    try {
      const raw = localStorage.getItem('operadorAutenticado');
      if (raw) {
        const op = JSON.parse(raw);
        return op.nombre || op.id || 'Agent';
      }
    } catch (e) { }
    return 'Agent';
  })();

  // Listener para lista de chats
  useEffect(() => {
    const q = query(collection(db, 'chats'), orderBy('lastMessageAt', 'desc'));
    const unsub = onSnapshot(q, snap => {
      const arr = snap.docs.map(d => ({ phone: d.id, ...d.data() }));
      setChats(arr);
      if (arr.length > 0 && !selectedPhone) {
        setSelectedPhone(arr[0].phone);
      }
    });

    return () => unsub();
  }, [selectedPhone]);

  // Listener para mensajes del chat seleccionado
  useEffect(() => {
    if (!selectedPhone) {
      setMessages([]);
      return;
    }

    const chatRef = doc(db, 'chats', selectedPhone);
    const unsub = onSnapshot(chatRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const rawMessages = data.messages || [];

        // Mapear los mensajes de la estructura { messageData: {...} } a la estructura plana que usa la UI
        const mappedMessages = rawMessages.map((m, index) => {
          const md = m.messageData || {};
          return {
            id: md.messageId || index,
            from: md.from, // 'client', 'bot', 'agent'
            body: md.content,
            type: md.type,
            timestamp: md.timestamp,
            time: md.time, // Extract backup time field
            status: md.status
          };
        });

        setMessages(mappedMessages);
      } else {
        setMessages([]);
      }
    });

    return () => unsub();
  }, [selectedPhone]);

  const sendMessage = async () => {
    if (!text.trim() || !selectedPhone) return;

    setSending(true);
    setError('');
    try {
      const chatRef = doc(db, 'chats', selectedPhone);
      const now = new Date();

      const newMessage = {
        date: now.toLocaleDateString('en-US'), // Formato M/D/YYYY como en el ejemplo
        messageData: {
          content: text.trim(),
          direction: 'outgoing',
          from: 'agent',
          processed: true,
          type: 'text',
          messageId: `msg-${Date.now()}`,
          timestamp: new Date(), // Usar Date directo, Firestore lo convertirá si es necesario o usar Timestamp.now()
          status: 'sent',
          time: now.toLocaleTimeString('es-EC', { hour12: false }),
          messageType: 'outgoing'
        }
      };


      await updateDoc(chatRef, {
        messages: arrayUnion(newMessage),
        lastMessage: text.trim(),
        lastMessageAt: serverTimestamp() // o new Date()
      });

      // Enviar por API WhatsApp (nuevo endpoint)
      try {
        const to = normalizarTelefonoParaWhatsAppApi(selectedPhone);
        const apiUrl = getWhatsAppSendUrl();
        const res = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'content-type': 'application/json', ...(await cabeceraSesion()) },
          body: JSON.stringify({ to, message: text.trim() })
        });
        if (!res.ok) {
          const t = await res.text();
          console.error('API WhatsApp ChatPanel:', res.status, t);
          setError('No se pudo enviar el mensaje por WhatsApp.');
        }
      } catch (e) {
        console.error('Error enviando a API WhatsApp:', e);
        setError('No se pudo enviar el mensaje por WhatsApp.');
      }

      setText('');
    } catch (e) {
      console.error('Error sending message:', e);
      setError('No se pudo guardar/enviar el mensaje.');
    } finally {
      setSending(false);
    }
  };

  const selectedChat = chats.find(c => c.phone === selectedPhone);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{ padding: 12, borderBottom: '1px solid #e5e7eb', background: '#fff', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#1f2937' }}>
            {selectedChat?.name || 'Selecciona un chat'}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            {selectedChat?.phone || ''}
          </div>
        </div>
      </div>
      {error && (
        <div style={{ padding: 10, background: '#fff1f2', borderBottom: '1px solid #fecdd3', color: '#be123c', fontSize: 12 }}>
          {error}
        </div>
      )}

      {/* Main content: List + Messages */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <ChatList chats={chats} selectedPhone={selectedPhone} onSelectPhone={setSelectedPhone} />

        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
          <MessageList messages={messages} currentAgent={currentAgent} />

          {/* Composer */}
          {selectedPhone && (
            <div style={{ padding: 10, borderTop: '1px solid #ccc', background: '#fff', display: 'flex', gap: 8 }}>
              <input
                value={text}
                onChange={e => setText(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Escribe un mensaje..."
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  fontSize: 14,
                  outline: 'none'
                }}
              />
              <button
                onClick={sendMessage}
                disabled={sending || !text.trim()}
                style={{
                  padding: '10px 16px',
                  background: sending || !text.trim() ? '#d1d5db' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  cursor: sending || !text.trim() ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  fontSize: 14
                }}
              >
                {sending ? '...' : 'Enviar'}
              </button>
            </div>
          )}

          {!selectedPhone && (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
              Selecciona un chat para ver los mensajes
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
